import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '@/features/auth/config/authConfig.ts';

// Singleton instance.
// MsalProvider (msal-react v5) calls initialize() and handleRedirectPromise() internally —
// do NOT call them manually here to avoid double-initialization race conditions.
export const msalInstance = new PublicClientApplication(msalConfig);

// ---------------------------------------------------------------------------
// Stale-cache invalidation after a redeploy
// ---------------------------------------------------------------------------
// __APP_VERSION__ is injected by Vite at build time (see vite.config.ts).
// It is currently a pure build timestamp ("build-<ms>") because package.json
// version is not actively maintained/bumped.
// TODO: once package.json version is properly bumped on each release, switch
//       vite.config.ts to use `${pkg.version}-${Date.now()}` for a more
//       meaningful identifier.
const APP_VERSION_KEY = 'app_build_version';
const CURRENT_BUILD_HASH: string =
  (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null) ?? 'unknown';

/**
 * Clear all MSAL-managed cache entries using the official API.
 * Prefer this over manually iterating localStorage keys.
 */
export const clearMsalCache = async (): Promise<void> => {
  await msalInstance.clearCache();
};

export const checkAndClearStaleCache = async (): Promise<void> => {
  try {
    // If MSAL is currently processing a redirect (e.g., returning from Azure AD),
    // it stores an `interaction_in_progress` flag in sessionStorage.  Clearing the
    // cache at this point would destroy the state that handleRedirectPromise needs,
    // silently failing the login.  Skip the version check in that case.
    const hasInteractionInProgress = Object.keys(sessionStorage).some(key =>
      key.includes('interaction_in_progress')
    );
    if (hasInteractionInProgress) {
      console.log('MSAL interaction in progress — skipping stale-cache check');
      return;
    }

    const storedVersion = localStorage.getItem(APP_VERSION_KEY);

    if (storedVersion && storedVersion !== CURRENT_BUILD_HASH) {
      console.log(
        `App version changed (${storedVersion} → ${CURRENT_BUILD_HASH}), clearing MSAL cache`
      );

      // Save the app version before clearing MSAL cache
      const newVersion = CURRENT_BUILD_HASH;

      // Clear MSAL cache
      await clearMsalCache();

      // Restore the app version key after MSAL clears localStorage
      localStorage.setItem(APP_VERSION_KEY, newVersion);

      console.log('Stale MSAL cache cleared');
    } else if (!storedVersion) {
      // First time visit or localStorage was cleared
      localStorage.setItem(APP_VERSION_KEY, CURRENT_BUILD_HASH);
    }
  } catch (error) {
    console.error('Error checking app version:', error);
    // Ensure the version key is set even if there's an error
    try {
      localStorage.setItem(APP_VERSION_KEY, CURRENT_BUILD_HASH);
    } catch (e) {
      console.error('Failed to set app version key:', e);
    }
  }
};
